import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { FADE_IN_VARIANTS } from "../../../../constants/animations";

/**
 * FadeIn - Simple fade-in animation wrapper for page/section entrances
 * 
 * Wraps content with a smooth fade-in and slide-up animation.
 * Perfect for page sections, cards, or any content that should
 * gracefully appear on mount.
 * 
 * @component
 * @example
 * ```jsx
 * import { FadeIn } from "../ui/Motion";
 * 
 * // Basic usage
 * <FadeIn>
 *   <div>This content will fade in</div>
 * </FadeIn>
 * 
 * // With delay
 * <FadeIn delay={0.3}>
 *   <p>This appears after 0.3s</p>
 * </FadeIn>
 * 
 * // As different element
 * <FadeIn as="section" className="hero">
 *   <h1>Hero Title</h1>
 * </FadeIn>
 * ```
 */
export const FadeIn = ({
    children,
    delay = 0,
    duration = 0.3,
    as = "div",
    className,
    style,
    ...props
}) => {
    const MotionComponent = motion[as] || motion.div;

    return (
        <MotionComponent
            initial="hidden"
            animate="visible"
            variants={FADE_IN_VARIANTS}
            transition={{ delay, duration }}
            className={className}
            style={style}
            {...props}
        >
            {children}
        </MotionComponent>
    );
};

FadeIn.propTypes = {
    children: PropTypes.node.isRequired,
    delay: PropTypes.number,
    duration: PropTypes.number,
    as: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object
};

export default FadeIn;
